/**
 * PawLink consent-mode gate.
 *
 * A tiny, dependency-free store that tracks whether the user has granted
 * "analytics_marketing" consent (right now the only category we ask for —
 * we can split it into buckets later if regulatory scope grows).
 *
 * States:
 *   "granted"  → analytics/marketing scripts (TikTok Pixel) may load & fire
 *   "denied"   → nothing loads; existing pixel is prevented from firing
 *   "unset"    → user has not yet made a choice; ConsentBanner is shown
 *
 * The value is persisted in localStorage under `pawlink_consent_v1` and
 * broadcast via a CustomEvent(`pawlink:consent`) so any listener (pixel
 * loader, router page-view tracker) can react.
 *
 * Consent can be withdrawn at any time from the footer "Cookie preferences"
 * link — that reopens the banner and lets the user flip their choice.
 *
 * Note: setting state to "denied" AFTER the pixel has loaded flushes the
 * TikTok queue and no further events are sent. TikTok's SDK doesn't expose
 * a clean uninstall, so the loaded script itself stays in memory until the
 * next page navigation, but no tracking calls will succeed.
 */

const STORAGE_KEY = "pawlink_consent_v1";
const EVENT_NAME = "pawlink:consent";

export const CONSENT_GRANTED = "granted";
export const CONSENT_DENIED = "denied";
export const CONSENT_UNSET = "unset";

let _memoryCache = null;

export function getConsent() {
    if (_memoryCache) return _memoryCache;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === CONSENT_GRANTED || raw === CONSENT_DENIED) {
            _memoryCache = raw;
            return raw;
        }
    } catch { /* localStorage may be blocked */ }
    return CONSENT_UNSET;
}

export function setConsent(value) {
    if (value !== CONSENT_GRANTED && value !== CONSENT_DENIED) return;
    _memoryCache = value;
    try { localStorage.setItem(STORAGE_KEY, value); } catch { /* noop */ }
    try {
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { value } }));
    } catch { /* noop */ }
}

/**
 * Reset consent back to "unset" — reopens the banner and stops future
 * pixel calls. Used by the "Cookie preferences" link.
 */
export function resetConsent() {
    _memoryCache = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    try {
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { value: CONSENT_UNSET } }));
    } catch { /* noop */ }
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 */
export function onConsentChange(callback) {
    const handler = (e) => callback(e?.detail?.value ?? getConsent());
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
}
